import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const faqData = [
  { question: 'How is Forsyt different from Clari?', answer: 'Clari focuses on revenue forecasting and pipeline inspection. Forsyt is a Revenue OS that goes beyond forecasting to prescribe specific next actions for each rep, based on signals from CRM, calls, email, and Slack.' },
  { question: 'Can Forsyt replace Clari?', answer: 'Forsyt complements Clari. Clari tells you what your forecast looks like; Forsyt tells each rep what to do right now to improve that forecast by unblocking stalled deals.' },
  { question: 'What does Forsyt do that Clari doesn\'t?', answer: 'Forsyt identifies the one deal that matters most for each rep, pinpoints the real blocker, and prescribes the exact action. Clari provides pipeline analytics and forecast accuracy.' },
];

const ForsytVsClari = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Forsyt vs Clari - Revenue OS vs Revenue Forecasting"
        description="Clari forecasts your pipeline. Forsyt tells your reps what to do about it. Compare Revenue OS vs forecasting tools to find what your sales team needs."
        path="/vs-clari"
        faq={faqData}
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img
                src="/assets/gt687tca.png"
                alt="Forsyt"
                className="h-8 w-auto"
              />
            </div>
            <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-5xl font-bold text-gray-900 mb-8">Forsyt vs Clari: From Forecast Accuracy To Execution Accuracy</h1>

          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Introduction</h2>
              <p className="text-gray-700 leading-relaxed">
                Clari is synonymous with revenue forecasting for thousands of companies, offering a powerful way to roll up pipeline, inspect trends, and answer "Will we hit the number?". Forsyt starts where Clari stops: enforcing the actions that actually make that number real.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">What Clari Is Designed To Do</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Clari's core promise is predictable revenue through better forecasting, inspection, and pipeline visibility.
              </p>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Clari's Strengths</h3>
              <ul className="list-disc pl-6 space-y-3 text-gray-700">
                <li>Aggregates data from CRM, email, calendars, and other systems into a unified revenue view.</li>
                <li>Provides forecast roll‑ups across segments, products, geos, and time horizons.</li>
                <li>Highlights risk and upside deals based on execution history and live signals.</li>
                <li>Helps leaders run forecast calls and QBRs with a single "source of truth" dashboard.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>If your big question is "What's our number going to be?", Clari is built for that.</strong>
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">The Forecasting Gap, Knowing vs Doing</h2>
              <p className="text-gray-700 leading-relaxed mb-4">Where many Clari customers still struggle:</p>
              <ul className="list-disc pl-6 space-y-3 text-gray-700">
                <li>The forecast is accurate, but deals still slip because reps didn't run the right plays in time.</li>
                <li>Leaders still hold long inspection calls to ask "Did you multi‑thread? Did you follow up after procurement?"</li>
                <li>Forecasting becomes a mirror of rep inputs and discipline; it doesn't automatically upgrade them.</li>
                <li>Dashboards sharpen visibility; they don't enforce behavior.</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Forsyt, The System of Action That Sits On Top</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Forsyt treats Clari (or any forecasting tool) and CRM as systems of record and analytics, then adds a system of action on top.
              </p>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4">How Forsyt Complements a Forecasting Stack</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Action cards for every rep</h4>
                  <p className="text-gray-700">For each opportunity, Forsyt uses cross‑tool signals to create a "next best action" card with the who, what, and why, then tracks completion.</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Process enforcement, not just visibility</h4>
                  <p className="text-gray-700">Multi‑threading, MEDDIC checkpoints, mutual action plans, and exit criteria become non‑negotiable because Forsyt nudges and logs them.</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Execution‑aware forecasting</h4>
                  <p className="text-gray-700">When you look at a forecast, you can see not just deal amount and stage, but whether the Forsyt‑recommended actions were actually done.</p>
                </div>
              </div>
              
              <div className="p-6 bg-blue-50 border-l-4 border-blue-600 my-8">
                <p className="text-lg italic text-gray-800">
                  "Forecasting tells you where you'll land. A Revenue OS tells your team how to change that trajectory this week."
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Forsyt vs Clari, Key Differences</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-300">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border border-gray-300 px-4 py-3 text-left font-bold">Dimension</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-bold">Clari (Forecasting Platform)</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-bold">Forsyt (Revenue Operating System)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Primary question</td>
                      <td className="border border-gray-300 px-4 py-3">"What will we close?"</td>
                      <td className="border border-gray-300 px-4 py-3">"What must we do, this week, to change that?"</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Core user</td>
                      <td className="border border-gray-300 px-4 py-3">CRO, RevOps, Finance, Sales leaders.</td>
                      <td className="border border-gray-300 px-4 py-3">CRO, managers, and reps in the trenches.</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Data focus</td>
                      <td className="border border-gray-300 px-4 py-3">Historical trends + live pipeline metrics.</td>
                      <td className="border border-gray-300 px-4 py-3">Behavioral + conversational signals across tools.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Output</td>
                      <td className="border border-gray-300 px-4 py-3">Forecasts, dashboards, inspection views.</td>
                      <td className="border border-gray-300 px-4 py-3">Action queues, nudges, play enforcement, and "do this next" cards.</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Relationship to Clari</td>
                      <td className="border border-gray-300 px-4 py-3">N/A</td>
                      <td className="border border-gray-300 px-4 py-3">Designed to plug into your stack alongside Clari, not replace it.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">When To Add Forsyt On Top Of Clari</h2>
              <ol className="list-decimal pl-6 space-y-4 text-gray-700">
                <li>
                  <strong>Your forecast meetings are long and subjective.</strong><br />
                  If you still spend time arguing deal reality, you need a system that ties actions and conversations directly into the inspection view.
                </li>
                <li>
                  <strong>You have Clari dashboards, but still chase reps for updates.</strong><br />
                  That's a sign your system of record and system of action are disconnected.
                </li>
                <li>
                  <strong>Your board trusts the forecast, but not the plan.</strong><br />
                  Forsyt gives you a way to show not only "Here's the number," but "Here's the exact set of plays we're running to protect it."
                </li>
              </ol>
            </section>

            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Recommendation</h3>
              <p className="mb-6">
                Keep Clari as your revenue "altimeter". Add Forsyt as your "autopilot" that ensures reps actually fly the route.
              </p>
              <a 
                href="https://calendar.app.google/nswB9pt6ALLZ8xoy7" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300"
              >
                Book Your Demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ForsytVsClari;
